import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useSendEmailMfaCode, useVerifyEmailMfa } from '@/hooks/useAuthMutations';
import { LoginResult } from '@vivport/backend/types';
import { Loader2, Mail, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Notice } from '@/components/ui/notice';

interface LoginMfaEmailCodeProps {
  mfaToken: string;
  onSuccess: (result: LoginResult) => void | Promise<void>;
  onError?: (error: string) => void;
  onBack?: () => void;
}

export function LoginMfaEmailCode({ mfaToken, onSuccess, onError, onBack }: LoginMfaEmailCodeProps) {
  const [emailCode, setEmailCode] = useState('');
  const hasInitialSent = useRef(false);

  const emailVerifyMutation = useVerifyEmailMfa();
  const sendEmailCodeMutation = useSendEmailMfaCode();

  // Automatically send email code when component loads (only once)
  useEffect(() => {
    if (mfaToken && !hasInitialSent.current) {
      hasInitialSent.current = true;
      sendEmailCodeMutation.mutate(mfaToken);
    }
  }, [mfaToken, sendEmailCodeMutation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailCode.trim()) return;
    sendEmailCodeMutation.reset();
    emailVerifyMutation.reset();
    emailVerifyMutation.mutate({ mfaToken, code: emailCode }, {
      onSuccess,
      onError: (error) => onError?.(error.message),
    });
  };

  
  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <div className="flex items-center justify-center mb-4">
          <Mail className="h-12 w-12" />
        </div>
        <CardTitle className="text-center">Email Verification</CardTitle>
        <CardDescription className="text-center">
          Enter the verification code sent to your email address
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="text"
            placeholder="Enter verification code"
            value={emailCode}
            onChange={(e) => setEmailCode(e.target.value)}
            required
            disabled={emailVerifyMutation.isPending}
            className="text-center text-lg tracking-widest"
            maxLength={6}
          />
          
          <Button 
            type="submit" 
            className="w-full"
            disabled={emailVerifyMutation.isPending || !emailCode.trim()}
          >
            {emailVerifyMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>
        </form>

        {emailVerifyMutation.isError && (
          <Notice tone="destructive" className="mt-4 rounded p-3">
            {emailVerifyMutation.error?.message || 'Failed to verify code. Please try again.'}
          </Notice>
        )}

        <div className="space-y-2 mt-4">
          <Button
            variant="outline"
            onClick={() => sendEmailCodeMutation.mutate(mfaToken)}
            disabled={sendEmailCodeMutation.isPending}
            className="w-full"
          >
            {sendEmailCodeMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Resend Code
              </>
            )}
          </Button>

          {onBack && (
            <Button
              variant="ghost"
              onClick={onBack}
              disabled={emailVerifyMutation.isPending || sendEmailCodeMutation.isPending}
              className="w-full"
            >
              Back to Login
            </Button>
          )}
        </div>

        {(sendEmailCodeMutation.isSuccess || sendEmailCodeMutation.isError) && (
          <Notice tone={sendEmailCodeMutation.isSuccess ? 'success' : 'destructive'} className="mt-4 rounded p-3">
            {sendEmailCodeMutation.isSuccess 
              ? sendEmailCodeMutation.data?.message || 'Verification code sent to your email.'
              : sendEmailCodeMutation.error?.message || 'Failed to send verification code.'
            }
          </Notice>
        )}
      </CardContent>
    </Card>
  );
} 